import { RSAA, type RSAAAction } from 'redux-api-middleware';

import {
  buildAddressesUrl,
  buildLandlordPaymentsUrlV2,
  buildRentalProtectionUrl,
  buildUserProfileUrl,
  GET,
  POST,
} from '@common/paymentsUtils/api';
import {
  REQUEST_CANCEL_MOVE_IN_COSTS,
  REQUEST_CANCEL_RENT_AMOUNT_CHANGE,
  REQUEST_COVERAGE_REQUEST_SIGNING_CANCELLED,
  REQUEST_CREATE_MOVE_IN_COSTS,
  REQUEST_CREATE_ONE_TIME_PAYMENT,
  REQUEST_CREATE_PAYMENT_PLAN_INSTALLMENT,
  REQUEST_CREATE_RENT_TERMS,
  REQUEST_DELETE_ONE_TIME_PAYMENT,
  REQUEST_DELETE_PAYMENT_PLAN,
  REQUEST_EMBEDDED_COVERAGE_REQUEST_URL,
  REQUEST_GET_FEATURE_PERMISSIONS,
  REQUEST_GET_ID_VERIFICATION_INFO,
  REQUEST_GET_USER_ADDRESS,
  REQUEST_HOSTED_ONBOARDING_URL,
  REQUEST_ID_VERIFICATION_QUESTIONS,
  REQUEST_LANDLORD_STATUS,
  REQUEST_LANDLORD_STATUS_SETUP_NEW_TERM,
  REQUEST_LINK_TOKEN,
  REQUEST_MARK_ACKNOWLEDGED,
  REQUEST_MARK_COVERAGE_REQUEST_SIGNED,
  REQUEST_PHONE_NUMBER_FROM_LISTING,
  REQUEST_PHONE_VERIFICATION_CHECK,
  REQUEST_PHONE_VERIFICATION_CONFIRM,
  REQUEST_PHONE_VERIFICATION_SEND,
  REQUEST_RENT_AMOUNT_CHANGE,
  REQUEST_RENTAL_PROTECTION_LEGAL_DISCLAIMER,
  REQUEST_SAVE_RENTAL_RELATIONSHIP_ID_TO_POLICY,
  REQUEST_SET_USER_ADDRESS,
  REQUEST_STOP_PAYMENTS,
  REQUEST_SUBMIT_FEEDBACK,
  REQUEST_SUBMIT_ID_VERIFICATION_ANSWERS,
  REQUEST_UPDATE_RENT_TERMS,
  REQUEST_UPDATE_TENANT,
  REQUEST_USER_API,
  REQUEST_VERIFY_ADDRESS,
} from '@rental-manager/constants/ActionConstants';
import LandlordStatusModel from '@rental-manager/models/LandlordStatus';
import FeaturePermissionModel from '@rental-manager/models/FeaturePermission';
import IdVerificationInfoModel from '@rental-manager/models/IdVerificationInfo';
import CoverageRequestEmbeddedUrlModel from '@rental-manager/models/CoverageRequestEmbeddedUrl';
import RentalProtectionPolicy from '@rental-manager/models/RentalProtectionPolicy';
import RentalProtectionDisclaimers from '@rental-manager/models/RentalProtectionDisclaimers';
import ConfigModel from '@rental-manager/models/Config';
import StringConstants from '@common/constants/StringConstants';
import { formatYMDStringToTimestamp } from '@common/paymentsUtils/dateUtils';
import UserAddress from '@rental-manager/models/UserAddress';
import VerifyAddress from '@rental-manager/models/VerifiedAddressResponse';
import UrlModel from '@rental-manager/models/Url';

const rentalManagerProxy = StringConstants.API.RENTAL_MANAGER.PROXY;
const rentalManagerApi = StringConstants.API.RENTAL_MANAGER.API;
const rentalManagerApiV1 = rentalManagerProxy + rentalManagerApi;
const requestHeader = {
  'Content-Type': 'application/json',
};

export const getUserLandlord = ({ requireStrongAuthentication }): RSAAAction => ({
  [RSAA]: {
    endpoint: `${rentalManagerApiV1}/get?pageWithSensitiveInformation=${requireStrongAuthentication}`,
    method: GET,
    headers: requestHeader,
    credentials: 'same-origin',
    types: [{ type: REQUEST_USER_API.INIT }, { type: REQUEST_USER_API.SUCCESS }, { type: REQUEST_USER_API.FAIL }],
  },
});

export const getLandlordStatus = ({ propertyId, encodedLeaseId = null, setupNewTerm = false }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/getLandlordStatus'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      encodedLeaseId,
    }),
    credentials: 'same-origin',
    /*
        Dispatching actions for Setup New Term flow
        to prevent unmounting and re-mounting of Homepage Component
        */
    types: setupNewTerm
      ? [
          {
            type: REQUEST_LANDLORD_STATUS_SETUP_NEW_TERM.INIT,
            meta: { propertyId },
          },
          {
            type: REQUEST_LANDLORD_STATUS_SETUP_NEW_TERM.SUCCESS,
            meta: { propertyId },
            payload: (action, state, res) =>
              res.json().then((json: any = {}) => ({
                data: LandlordStatusModel(json.data),
                config: ConfigModel(json.config),
              })),
          },
          {
            type: REQUEST_LANDLORD_STATUS_SETUP_NEW_TERM.FAIL,
            meta: { propertyId },
          },
        ]
      : [
          {
            type: REQUEST_LANDLORD_STATUS.INIT,
            meta: { propertyId },
          },
          {
            type: REQUEST_LANDLORD_STATUS.SUCCESS,
            meta: { propertyId },
            payload: (action, state, res) =>
              res.json().then((json: any = {}) => ({
                data: LandlordStatusModel(json.data),
                config: ConfigModel(json.config),
              })),
          },
          {
            type: REQUEST_LANDLORD_STATUS.FAIL,
            meta: { propertyId },
          },
        ],
  },
});

export const getPlaidLinkToken = ({ propertyId }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/createLinkToken'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_LINK_TOKEN.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_LINK_TOKEN.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: ConfigModel(json.data),
          })),
      },
      {
        type: REQUEST_LINK_TOKEN.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const updateTenantInfo = ({ propertyId, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/updateRenterInfoByLandlord'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_UPDATE_TENANT.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_UPDATE_TENANT.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_UPDATE_TENANT.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const requestRentAmountChange = ({ propertyId, rentTermsKey, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/requestRentAmountChange'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      rentTermsKey,
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_RENT_AMOUNT_CHANGE.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_RENT_AMOUNT_CHANGE.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) => res.json(),
      },
      {
        type: REQUEST_RENT_AMOUNT_CHANGE.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const cancelRentAmountChange = ({ propertyId, rentTermsKey, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/cancelRentAmountChange'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      rentTermsKey,
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_CANCEL_RENT_AMOUNT_CHANGE.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_CANCEL_RENT_AMOUNT_CHANGE.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) => res.json(),
      },
      {
        type: REQUEST_CANCEL_RENT_AMOUNT_CHANGE.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const stopPayments = ({ propertyId, rentTermsKey }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/stopPayment'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      rentTermsKey,
      resourceId: propertyId,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_STOP_PAYMENTS.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_STOP_PAYMENTS.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_STOP_PAYMENTS.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const createOneTimePayment = ({ propertyId, rentTermsKey, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/createOneTimePaymentRequest'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      rentTermsKey,
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_CREATE_ONE_TIME_PAYMENT.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_CREATE_ONE_TIME_PAYMENT.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_CREATE_ONE_TIME_PAYMENT.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const createMoveInCosts = ({ propertyId, dueDate, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/createMoveInCostsRequest'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      dueDate: formatYMDStringToTimestamp(dueDate),
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_CREATE_MOVE_IN_COSTS.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_CREATE_MOVE_IN_COSTS.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_CREATE_MOVE_IN_COSTS.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const createPaymentPlanRequest = ({ propertyId, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/createPaymentPlanInstallment'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_CREATE_PAYMENT_PLAN_INSTALLMENT.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_CREATE_PAYMENT_PLAN_INSTALLMENT.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_CREATE_PAYMENT_PLAN_INSTALLMENT.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const deleteOneTimePayment = ({ propertyId, groupKey, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/deleteOneTimePaymentGroup'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      groupKey,
      resourceId: propertyId,
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_DELETE_ONE_TIME_PAYMENT.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_DELETE_ONE_TIME_PAYMENT.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_DELETE_ONE_TIME_PAYMENT.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const cancelMoveInCosts = ({ propertyId, groupKey, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/deleteOneTimePaymentGroup'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      groupKey,
      resourceId: propertyId,
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_CANCEL_MOVE_IN_COSTS.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_CANCEL_MOVE_IN_COSTS.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_CANCEL_MOVE_IN_COSTS.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const deletePaymentPlan = ({ propertyId, requestKey, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/deleteOneTimePaymentRequest'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      requestKey,
      resourceId: propertyId,
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_DELETE_PAYMENT_PLAN.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_DELETE_PAYMENT_PLAN.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_DELETE_PAYMENT_PLAN.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const requestMarkAcknowledged = ({ propertyId, actionId }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/markAcknowledged'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({ propertyId, actionId }),
    credentials: 'same-origin', // add cookies to requests of same origin
    types: [
      {
        type: REQUEST_MARK_ACKNOWLEDGED.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_MARK_ACKNOWLEDGED.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_MARK_ACKNOWLEDGED.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const requestUpdateRentTerms = ({ propertyId, ...payload }): RSAAAction => {
  if (payload?.firstDueDate) {
    payload.firstDueDate = formatYMDStringToTimestamp(payload.firstDueDate);
  }
  if (payload?.rentTerms?.firstDueDate) {
    payload.rentTerms.firstDueDate = formatYMDStringToTimestamp(payload.rentTerms.firstDueDate);
  }
  return {
    [RSAA]: {
      endpoint: buildLandlordPaymentsUrlV2('/updateRentTerms'),
      method: POST,
      headers: requestHeader,
      body: (state: Record<string, any>): string =>
        JSON.stringify({
          userId: state.user.userToken,
          resourceId: propertyId,
          authZone: 'zillow',
          ...payload,
        }),
      credentials: 'same-origin',
      types: [
        {
          type: REQUEST_UPDATE_RENT_TERMS.INIT,
          meta: { propertyId },
        },
        {
          type: REQUEST_UPDATE_RENT_TERMS.SUCCESS,
          meta: { propertyId },
        },
        {
          type: REQUEST_UPDATE_RENT_TERMS.FAIL,
          meta: { propertyId },
        },
      ],
    },
  };
};

export const requestCreateNewTerm = ({ propertyId, firstDueDate, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/createRentTerms'),
    method: POST,
    headers: requestHeader,
    body: (state: Record<string, any>): string =>
      JSON.stringify({
        userId: state.user.userToken,
        resourceId: propertyId,
        authZone: 'zillow',
        firstDueDate: formatYMDStringToTimestamp(firstDueDate),
        ...payload,
      }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_CREATE_RENT_TERMS.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_CREATE_RENT_TERMS.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_CREATE_RENT_TERMS.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const requestUpdateListingPhoneNumber = ({ propertyId, phone }): RSAAAction => ({
  [RSAA]: {
    endpoint: `${rentalManagerApiV1}/properties/phone?propertyId=${propertyId}&phone=${phone}`,
    method: GET,
    headers: requestHeader,
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_PHONE_NUMBER_FROM_LISTING.INIT,
      },
      {
        type: REQUEST_PHONE_NUMBER_FROM_LISTING.SUCCESS,
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: json.response,
          })),
      },
      {
        type: REQUEST_PHONE_NUMBER_FROM_LISTING.FAIL,
      },
    ],
  },
});

export const requestPhoneVerificationCheck = ({ propertyId, phoneNumber }): RSAAAction => ({
  [RSAA]: {
    endpoint: `${rentalManagerApiV1}/phoneVerification/isVerified?phone=${phoneNumber}&verificationSource=otpRentPayment`,
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({}),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_PHONE_VERIFICATION_CHECK.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_PHONE_VERIFICATION_CHECK.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_PHONE_VERIFICATION_CHECK.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const requestPhoneVerificationSend = ({ propertyId, phoneNumber, verificationMethod }): RSAAAction => ({
  [RSAA]: {
    endpoint:
      `${rentalManagerApiV1}/phoneVerification/sendVerificationCode?phone=${phoneNumber}` +
      `&verificationSource=otpRentPayment&verificationType=${verificationMethod}`,
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({}),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_PHONE_VERIFICATION_SEND.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_PHONE_VERIFICATION_SEND.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_PHONE_VERIFICATION_SEND.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const requestPhoneVerificationConfirm = ({ propertyId, phoneNumber, verificationCode }): RSAAAction => ({
  [RSAA]: {
    endpoint:
      `${rentalManagerApiV1}/phoneVerification/validateVerificationCode?verificationSource=otpRentPayment` +
      `&phone=${phoneNumber}&verificationCode=${verificationCode}`,
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({}),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_PHONE_VERIFICATION_CONFIRM.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_PHONE_VERIFICATION_CONFIRM.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_PHONE_VERIFICATION_CONFIRM.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const submitFeedback = ({ propertyId, feedback, topic }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/submitFeedback'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      feedback,
      topic,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_SUBMIT_FEEDBACK.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_SUBMIT_FEEDBACK.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_SUBMIT_FEEDBACK.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const getFeaturePermissions = ({ propertyId }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/getFeaturePermissions'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_GET_FEATURE_PERMISSIONS.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_GET_FEATURE_PERMISSIONS.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: FeaturePermissionModel(json.data),
          })),
      },
      {
        type: REQUEST_GET_FEATURE_PERMISSIONS.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const requestIdVerificationQuestions = ({ propertyId, ...payload }): RSAAAction => ({
  [RSAA]: {
    endpoint: `${rentalManagerApiV1}/idVerification/performIdentityCheck`,
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      requestingClient: 'rent_payment',
      ...payload,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_ID_VERIFICATION_QUESTIONS.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_ID_VERIFICATION_QUESTIONS.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_ID_VERIFICATION_QUESTIONS.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const submitIdVerificationAnswers = ({ propertyId, answers = [] }): RSAAAction => ({
  [RSAA]: {
    endpoint: `${rentalManagerApiV1}/idVerification/submitIdentityAnswers`,
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      answers,
      requestingClient: 'rent_payment',
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_SUBMIT_ID_VERIFICATION_ANSWERS.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_SUBMIT_ID_VERIFICATION_ANSWERS.SUCCESS,
        meta: { propertyId },
      },
      {
        type: REQUEST_SUBMIT_ID_VERIFICATION_ANSWERS.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const getIdVerificationInfo = ({ propertyId }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/getIdVerificationInfo'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_GET_ID_VERIFICATION_INFO.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_GET_ID_VERIFICATION_INFO.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: IdVerificationInfoModel(json.data),
          })),
      },
      {
        type: REQUEST_GET_ID_VERIFICATION_INFO.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const getCoverageRequestEmbeddedUrl = ({ propertyId, paymentsEncodedLeaseId, rentTermsKey }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/getCoverageRequestEmbeddedUrl'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      paymentsEncodedLeaseId,
      rentTermsKey,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_EMBEDDED_COVERAGE_REQUEST_URL.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_EMBEDDED_COVERAGE_REQUEST_URL.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: CoverageRequestEmbeddedUrlModel(json.data),
          })),
      },
      {
        type: REQUEST_EMBEDDED_COVERAGE_REQUEST_URL.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const markCoverageRequestSigned = ({
  propertyId,
  paymentsEncodedLeaseId,
  documentId,
  quoteId,
  rentTermsKey,
}): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/markCoverageRequestSigned'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      paymentsEncodedLeaseId,
      documentId,
      quoteId,
      rentTermsKey,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_MARK_COVERAGE_REQUEST_SIGNED.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_MARK_COVERAGE_REQUEST_SIGNED.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: json.data,
          })),
      },
      {
        type: REQUEST_MARK_COVERAGE_REQUEST_SIGNED.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const signalCoverageRequestSigningCancelled = ({
  propertyId,
  paymentsEncodedLeaseId,
  rentTermsKey,
}): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/signalCoverageRequestSigningCancelled'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      paymentsEncodedLeaseId,
      rentTermsKey,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_COVERAGE_REQUEST_SIGNING_CANCELLED.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_COVERAGE_REQUEST_SIGNING_CANCELLED.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: json.data,
          })),
      },
      {
        type: REQUEST_COVERAGE_REQUEST_SIGNING_CANCELLED.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const getRentalProtectionDisclaimers = ({ propertyId, listingAlias }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildRentalProtectionUrl(`/getDisclaimers`),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      listingAlias,
      disclaimerTypes: 'entity',
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_RENTAL_PROTECTION_LEGAL_DISCLAIMER.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_RENTAL_PROTECTION_LEGAL_DISCLAIMER.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: RentalProtectionDisclaimers(json.response),
          })),
      },
      {
        type: REQUEST_RENTAL_PROTECTION_LEGAL_DISCLAIMER.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const saveRentalRelationshipIdToPolicy = ({ propertyId, paymentsEncodedLeaseId, rentTermsKey }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/saveRentalRelationshipIdToPolicy'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
      paymentsEncodedLeaseId,
      rentTermsKey,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_SAVE_RENTAL_RELATIONSHIP_ID_TO_POLICY.INIT,
        meta: { propertyId },
      },
      {
        type: REQUEST_SAVE_RENTAL_RELATIONSHIP_ID_TO_POLICY.SUCCESS,
        meta: { propertyId },
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: RentalProtectionPolicy(json.data.rentalPolicy),
          })),
      },
      {
        type: REQUEST_SAVE_RENTAL_RELATIONSHIP_ID_TO_POLICY.FAIL,
        meta: { propertyId },
      },
    ],
  },
});

export const getUserAddress = (): RSAAAction => ({
  [RSAA]: {
    endpoint: buildUserProfileUrl('/getUserAddress'),
    method: GET,
    headers: requestHeader,
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_GET_USER_ADDRESS.INIT,
      },
      {
        type: REQUEST_GET_USER_ADDRESS.SUCCESS,
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: UserAddress(json.response),
          })),
      },
      {
        type: REQUEST_GET_USER_ADDRESS.FAIL,
      },
    ],
  },
});

export const setUserAddress = (
  street: string,
  city: string,
  stateCode: string,
  zip: string,
  unit: string,
): RSAAAction => ({
  [RSAA]: {
    endpoint: buildUserProfileUrl('/setUserAddress'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      street,
      city,
      state: stateCode,
      zip,
      unit,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_SET_USER_ADDRESS.INIT,
      },
      {
        type: REQUEST_SET_USER_ADDRESS.SUCCESS,
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: UserAddress(json.response),
          })),
      },
      {
        type: REQUEST_SET_USER_ADDRESS.FAIL,
      },
    ],
  },
});

export const verifyAddress = (fullAddress: string): RSAAAction => ({
  [RSAA]: {
    endpoint: buildAddressesUrl(`/verification?fullAddress=${encodeURIComponent(fullAddress)}`),
    method: GET,
    headers: requestHeader,
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_VERIFY_ADDRESS.INIT,
      },
      {
        type: REQUEST_VERIFY_ADDRESS.SUCCESS,
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: VerifyAddress(json.response),
          })),
      },
      {
        type: REQUEST_VERIFY_ADDRESS.FAIL,
      },
    ],
  },
});

export const getHostedOnboardingUrl = ({ propertyId }): RSAAAction => ({
  [RSAA]: {
    endpoint: buildLandlordPaymentsUrlV2('/getHostedOnboardingUrl'),
    method: POST,
    headers: requestHeader,
    body: JSON.stringify({
      resourceId: propertyId,
    }),
    credentials: 'same-origin',
    types: [
      {
        type: REQUEST_HOSTED_ONBOARDING_URL.INIT,
      },
      {
        type: REQUEST_HOSTED_ONBOARDING_URL.SUCCESS,
        payload: (action, state, res) =>
          res.json().then((json: any = {}) => ({
            data: UrlModel(json.data),
          })),
      },
      {
        type: REQUEST_HOSTED_ONBOARDING_URL.FAIL,
      },
    ],
  },
});

/**
 * Execute >1 requests in parallel
 * Use this when feature permissions may have changed, as well as landlord status.
 */
export const getIreneDashboard = (props) => async (dispatch) =>
  Promise.all([await dispatch(getLandlordStatus(props)), await dispatch(getFeaturePermissions(props))]);
